'use client'

import { useRef } from 'react'
import { usePathname } from 'next/navigation'
import Link from 'next/link'
import { ChevronDownIcon, MapPinIcon } from '@heroicons/react/24/outline'
import { getCitiesByPriority } from '@/lib/data/gtaLocations'

const LOCATION_ITEMS = getCitiesByPriority().slice(0, 8).map((city) => ({
  name: city.displayName,
  href: `/locations/${city.slug}`,
}))

export default function LocationsDropdown({
  isOpen,
  onToggle,
  onOpen,
  onLeave,
  onClose,
  triggerRef,
}) {
  const pathname = usePathname()
  const openedFromPointerRef = useRef(false)
  const isLocationActive = pathname?.startsWith('/locations')

  return (
    <div
      className="relative"
      onPointerEnter={(event) => {
        if (event.pointerType === 'mouse') {
          openedFromPointerRef.current = !isOpen
          onOpen()
        }
      }}
      onPointerLeave={(event) => {
        if (event.pointerType === 'mouse') {
          openedFromPointerRef.current = false
          onLeave()
        }
      }}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) onClose()
      }}
    >
      <button
        ref={triggerRef}
        type="button"
        onClick={(event) => {
          if (event.detail > 0 && openedFromPointerRef.current) {
            openedFromPointerRef.current = false
            return
          }
          onToggle()
        }}
        className={`inline-flex min-h-10 items-center gap-1.5 whitespace-nowrap rounded-full px-3.5 text-[15px] font-medium tracking-[-0.01em] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-water-blue focus-visible:ring-offset-2 ${
          isOpen || isLocationActive
            ? 'bg-water-aqua text-water-blue-dark'
            : 'text-water-ink hover:bg-water-canvas hover:text-water-blue-dark'
        }`}
        aria-expanded={isOpen}
        aria-controls="desktop-locations-menu"
        aria-haspopup="true"
      >
        Locations
        <ChevronDownIcon
          className={`h-3.5 w-3.5 transition-transform duration-150 ${isOpen ? 'rotate-180' : ''}`}
          aria-hidden="true"
        />
      </button>

      {isOpen ? (
        <div
          id="desktop-locations-menu"
          className="absolute left-0 top-full z-50 w-[min(34rem,calc(100vw-2rem))] pt-3"
        >
          <div className="animate-nav-menu overflow-hidden rounded-2xl border border-water-border bg-white p-2 shadow-[0_16px_48px_rgba(11,34,57,0.10)]">
            <div className="flex items-start gap-3 px-3 pb-3 pt-2">
              <span className="flex h-9 w-9 flex-none items-center justify-center rounded-lg bg-water-aqua text-water-blue-dark">
                <MapPinIcon className="h-[1.125rem] w-[1.125rem]" aria-hidden="true" />
              </span>
              <div>
                <p className="text-sm font-semibold text-water-ink">Ontario Service Areas</p>
                <p className="mt-0.5 text-sm leading-5 text-water-muted">Local water guidance and professional service across the GTA.</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-1">
              {LOCATION_ITEMS.map((item) => {
                const isActive = pathname === item.href || pathname?.startsWith(`${item.href}/`)

                return (
                  <Link
                    key={item.href}
                    href={item.href}
                    onClick={onClose}
                    className={`rounded-xl px-3 py-2.5 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-water-blue ${
                      isActive
                        ? 'bg-water-aqua text-water-blue-dark'
                        : 'text-water-ink hover:bg-water-canvas hover:text-water-blue-dark'
                    }`}
                  >
                    {item.name}
                  </Link>
                )
              })}
            </div>
            <Link
              href="/locations"
              onClick={onClose}
              className="mt-2 flex min-h-11 items-center justify-between rounded-xl bg-water-ink px-4 text-sm font-semibold text-white transition-colors hover:bg-water-blue-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-water-blue focus-visible:ring-offset-2"
            >
              View All Service Areas
              <span aria-hidden="true">→</span>
            </Link>
          </div>
        </div>
      ) : null}
    </div>
  )
}
